import { 
    addHyphenToLicenseNumber, 
    calculateDrivingExperience,
    formatDate,
} from "../types/CommonType";
import { Driver, DriverInfo } from "../types/DriverTypes";
import { LicenseTypes } from "../types/LicenseTypes";

export enum LicenseCheckType {
    CAREER_RENT = "CAREER_RENT",            // 대여 가능 여부 운전경력 체크 필요
    CAREER_INSURANCE = "CAREER_INSURANCE",  // 보험 가입 가능 여부 운전경력 체크 필요
    LICENSE_TYPE = "LICENSE_TYPE",          // 면허종별 차량 대여 가능 여부 체크 필요
}

// 25.02.25. 면허증 현장 확인 필요 여부 체크
export const isNeedCheckLicense = (driverValue: DriverInfo, resInfo: any): LicenseCheckType[] => {
    const result = [];

    const licensNumberIncludeHyphen = addHyphenToLicenseNumber(driverValue.licenseNum);
    const driverCareer = calculateDrivingExperience(licensNumberIncludeHyphen).yearsOfExperience;

    if (resInfo.kioskAgeCheck == "BOTH" || resInfo.kioskAgeCheck == "MODEL") {
        const rentCondLicenseYearNumber = Number(resInfo.rentCondLicenseYear)

        // 02.25. 면허 상 경력과 차량 대여 기준 경력이 같은 경우, 면허증 발급일자 현장 확인 필요
        // 04.29. 면허 상 경력미달이어도, 관리자 승인으로 진행 가능하도록 수정
        if( driverCareer <= rentCondLicenseYearNumber ) {
            result.push(LicenseCheckType.CAREER_RENT);
        }
    }

    if (resInfo.kioskAgeCheck == "BOTH" || resInfo.kioskAgeCheck == "INSURANCE") {
        const insMinCareer = 
        resInfo && resInfo.carInsuranceChargeInfo.length > 0
            ? resInfo.carInsuranceChargeInfo[0].carInsuranceInfo.minCareer
            : 0;

        // 02.25. 면허 상 경력과 보험 가입 기준 경력이 같은 경우, 면허증 발급일자 현장 확인 필요
        // 04.29. 면허 상 경력미달이어도, 관리자 승인으로 진행 가능하도록 수정
        if( driverCareer <= insMinCareer ) {
            result.push(LicenseCheckType.CAREER_INSURANCE);
        }
    }

    // 25.08.22. 연령 체크가 보험 우선이지만 자차 미가입 일 경우 모델 경력으로 경력 체크
    if (resInfo.kioskAgeCheck == "INSURANCE" && resInfo && resInfo.carInsuranceChargeInfo.length == 0) {
        const rentCondLicenseYearNumber = Number(resInfo.rentCondLicenseYear)

        if( driverCareer <= rentCondLicenseYearNumber ) {
            result.push(LicenseCheckType.CAREER_RENT);
        }
    }

    // 대여 면허 종류 검증
    // 대여모델 : 1종 or 2종
    // 1. 모델이 1종이면? - 운전자가 01001 or 01003 중 1개면 가능
    // 2. 모델이 2종이면? - 운전자가 02002 or 02003 중 1개면 불가능
    const rentCondLicense = resInfo && resInfo.rentCondLicense;
    const permitLicenseType01 = ["01001", "01003"]; // 1종 대여 가능 유형
    const permitLicenseType02 = ["01001", "01003", "01004", "01005", "01006", "02001"]; // 2종 대여 가능 유형
    if(
       !((rentCondLicense === "01" && permitLicenseType01.includes(driverValue.licenseType)) ||
       (rentCondLicense === "02" && permitLicenseType02.includes(driverValue.licenseType))) 
    ) {
        result.push(LicenseCheckType.LICENSE_TYPE);
    }

    return result;
}

const parsingLicenseIssuedYear = (licenseNum: string): string => {
    const addedHyphenLicenseNum = addHyphenToLicenseNumber(licenseNum);
    const splitedLicenseNum = addedHyphenLicenseNum.split("-");

    if(splitedLicenseNum.length !== 4) { // 제대로된 면허증 규격이 아닌 경우 빈 문자열 return
      return "";
    }

    return splitedLicenseNum[1];
}

export const generateAdminCheckPopContent = (data: any, driver1st: DriverInfo | Driver, driver2nd?: DriverInfo | Driver) : string => {

    const targetCheckType: LicenseCheckType[] = [];
    Object.values(LicenseCheckType).forEach((checkType) => {
        if((!driver1st.isAdminManualCheck && driver1st.needCheckCareer.includes(checkType)) ||
            (driver2nd && !driver2nd.isAdminManualCheck && driver2nd.needCheckCareer.includes(checkType))) {
            targetCheckType.push(checkType);
        }
    });

    let popContent = "";
    targetCheckType.forEach((checkType) => {
        switch(checkType) {
            case LicenseCheckType.CAREER_RENT:
                popContent += "<br> - 경력조건 : 경력 만 " + data.rentCondLicenseYear + "년 이상"
                break;
            case LicenseCheckType.LICENSE_TYPE:
                popContent += "<br> - 면허조건 : 운전면허 " + data.rentCondLicenseString + " 취득"
                break;
            case LicenseCheckType.CAREER_INSURANCE:
                popContent += (data.carInsuranceChargeInfo.length > 0)
                            ? "<br> - 보험 가입 조건 : 만 " + data.carInsuranceChargeInfo[0].carInsuranceInfo.minCareer + "년 이상"
                            : "";
                break;
        }
    });

    popContent += "<br>";
    const driver1stLicenseIssuedYear = parsingLicenseIssuedYear(driver1st.licenseNum); 
    popContent += "<br> 주운전자 : " + driver1st.driverName + "(" + formatDate(driver1st.birthday || "") + ") / " + driver1stLicenseIssuedYear + "년도 " 

    const driver1stLicenseType = LicenseTypes.find((licenseType) => licenseType.erpCode === driver1st.licenseType);
    if(driver1stLicenseType) {
        popContent += driver1stLicenseType.description + " 취득";
    }

    if(driver2nd) {
        const driver2ndLicenseIssuedYear = parsingLicenseIssuedYear(driver2nd.licenseNum); 
        popContent += "<br> 부운전자 : " + driver2nd.driverName + "(" + formatDate(driver2nd.birthday || "") + ") / " + driver2ndLicenseIssuedYear + "년도 ";
        
        const driver2ndLicenseType = LicenseTypes.find((licenseType) => licenseType.erpCode === driver2nd.licenseType);
        if(driver2ndLicenseType) {
            popContent += driver2ndLicenseType.description + " 취득";
        }
    }
 
    return popContent;
}
